import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { BillingAccount, ClientAccount, RoleEnum, User } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import clientUtil from 'utils/clientUtil';

type TData = {
  sourceLanguageCodes: string[]; //"pt", "pt_BR",
  targetLanguageCodes: string[]; //"en", "en_GB",
  qualities: string[];
  styleGuides: {
    referenceId: number;
    name: string;
  }[];
  editors: {
    id: number;
    name: string;
  }[];
  isEditor: boolean;
  supportedFormats: string[];
  allowedTypes: string[];
  languagePairs: string[]; //'de-nl_NL', 'de-pl',
  currencies: string[];
  glossaries: {
    id: number;
    name: string;
  }[];
  wouldNeedDetails: boolean;
};

interface Payload {
  clientAccount: ClientAccount;
  currentUser: Pick<User, 'role'>;
  billingAccount: Pick<BillingAccount, 'allowedClientCurencies'>;
}

export const useNewTranslationRequest = (
  options: Omit<UseQueryOptions<unknown, TGenericError, TData>, 'queryKey'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query newTranslationRequest {
      clientAccount {
        allOrderableSourceLanguages {
          code
        }
        allOrderableTargetLanguages {
          code
        }
        languagePairs {
          sourceLanguageCode
          targetLanguageCode
        }
        orderableQualities
        styleGuides {
          referenceId
          name
        }
        members {
          # members => editors
          id
          # missing name, adding now
          name
        }
        supportedFormats
        allowedTypes
        glossaries {
          nodes {
            id
            name
          }
        }
        wouldNeedDetails
      }
      currentUser {
        role # isEditor role == "editor"
      }
      billingAccount {
        allowedClientCurencies
      }
    }
  `;

  return useQuery<unknown, TGenericError, TData>({
    ...options,
    queryKey: ['newTranslationRequest'],
    queryFn: async () => {
      const response: Payload = await client.request({
        data: {
          gql: graphql
        }
      });

      return {
        sourceLanguageCodes: response.clientAccount.allOrderableSourceLanguages?.map(
          (lang) => lang.code
        ),
        targetLanguageCodes: response.clientAccount.allOrderableTargetLanguages?.map(
          (lang) => lang.code
        ),
        qualities: response.clientAccount.orderableQualities,
        styleGuides: response.clientAccount.styleGuides,
        editors: response.clientAccount.members,
        isEditor: response?.currentUser?.role?.includes(RoleEnum.Editor),
        supportedFormats: response.clientAccount.supportedFormats,
        allowedTypes: response.clientAccount.allowedTypes,
        languagePairs: response.clientAccount.languagePairs?.map(
          (pair) => `${pair.sourceLanguageCode}-${pair.targetLanguageCode}`
        ),
        currencies: response.billingAccount.allowedClientCurencies,
        glossaries: response.clientAccount.glossaries?.nodes,
        wouldNeedDetails: response.clientAccount.wouldNeedDetails
      };
    }
  });
};
